import * as joint from '@joint/plus';
import * as appShapes from '../../shapes/app-shapes';
import ContextToolbarController from './controllers/contextToolbarController';
import {
  addElementsAndLink,
  listSubFunctions,
  applyOnSelection,
  changeSnapLines,
} from './controllers/elementsController';
import TriController from './controllers/triController';
import { PAPER_ZOOM_SETTINGS } from './helpers/constants';
import { initializeKeyboardShortcuts } from './initializers/keyboardShortcutsInitializer';
import { initializeNavigator } from './initializers/navigatorInitializer';
import { initializePaper } from './initializers/paperInitializer';
import { initializeSelection } from './initializers/selectionInitializer';
import { initializeStencil } from './initializers/stencilInitializer';
import { initializeToolsAndInspector } from './initializers/toolsInspectorInitializer';
import { initializeTooltips } from './initializers/tooltipInitializer';

class KitchenSinkService {
  constructor(
    el,
    paperContainer,
    { stencilService, inspectorService, haloService, keyboardService, navigatorService },
    { theme = 'csmdark', modules, readOnly = false }
  ) {
    joint.setTheme(theme);
    this.theme = theme;
    this.modules = modules;
    this.readOnly = readOnly;
    this.highlightedModules = [];
    this.paperContainer = paperContainer;
    this.defaultPaperSize = { width: 10583, height: 7560 };
    this.stencilService = stencilService;
    this.inspectorService = inspectorService;
    this.haloService = haloService;
    this.keyboardService = keyboardService;
    this.navigatorService = navigatorService;
    this.services = { inspectorService, stencilService, haloService, keyboardService, navigatorService };

    const view = new joint.mvc.View({ el });
    view.delegateEvents({ 'mouseup input[type="range"]': (evt) => evt.target.blur() });
    this.graph = new joint.dia.Graph({}, { cellNamespace: appShapes });

    this.renderContextToolbar = (point, selectedCells) => ContextToolbarController.render(this, point, selectedCells);

    window.rappidInstance = this;
  }

  setModules(newModules) {
    this.modules = newModules;
    this.inspectorService.setModules(newModules);
  }

  setHighlightedModules(newHighlightedModules) {
    this.highlightedModules = newHighlightedModules;
  }

  startRappid() {
    initializePaper(this);
    initializeStencil(this);
    initializeSelection(this);
    initializeToolsAndInspector(this);
    initializeNavigator(this);
    initializeKeyboardShortcuts(this);
    initializeTooltips(this);

    window.dispatchEvent(new CustomEvent('rappid:ready'));
  }

  addElementsAndLink() {
    addElementsAndLink(this);
  }

  listSubFunctions() {
    return listSubFunctions(this);
  }

  applyOnSelection(method) {
    applyOnSelection(this, method);
  }

  changeSnapLines(checked) {
    changeSnapLines(this, checked);
  }

  createTri() {
    return TriController.createTri(this);
  }

  handleElementSelect(cellView) {
    TriController.handleElementSelect(this, cellView);
  }

  updateTriPorts(triElement, newPorts) {
    TriController.updateTriPorts(this, triElement, newPorts);
  }

  zoomToFitFullMap() {
    if (!this.paperScroller) return;
    const graph = this.graph;

    if (!graph || graph.getElements().length === 0) return;

    const bbox = graph.getBBox();
    if (!bbox) return;

    let containerRect;
    if (document.fullscreenElement) {
      containerRect = document.fullscreenElement.getBoundingClientRect();
    } else {
      containerRect = this.paperContainer.getBoundingClientRect();
    }
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    const mapWidth = bbox.width;
    const mapHeight = bbox.height;
    const padding = 50;
    const targetZoomX = (containerWidth - padding) / mapWidth;
    const targetZoomY = (containerHeight - padding) / mapHeight;
    const targetZoom = Math.min(targetZoomX, targetZoomY);

    const constrainedZoom = Math.max(PAPER_ZOOM_SETTINGS.min, Math.min(PAPER_ZOOM_SETTINGS.max, targetZoom));
    this.paperScroller.zoom(constrainedZoom, { absolute: true });

    const centerX = bbox.x + bbox.width / 2;
    const centerY = bbox.y + bbox.height / 2;
    this.paperScroller.center(centerX, centerY);
  }

  zoomOutToMinimum() {
    if (!this.paperScroller) return;

    const graph = this.graph;

    if (!graph || graph.getElements().length === 0) return;

    const bbox = graph.getBBox();
    if (!bbox) return;

    const minZoom = PAPER_ZOOM_SETTINGS.min;
    this.paperScroller.zoom(minZoom, { absolute: true });

    const centerX = this.defaultPaperSize.width / 2;
    const centerY = this.defaultPaperSize.height / 2;
    this.paperScroller.center(centerX, centerY);
  }

  zoomOnHighlightedElements() {
    if (!this.paperScroller) return;

    const graph = this.graph;

    if (!graph || graph.getElements().length === 0) return;

    const highlightedElements = graph.getElements().filter((element) => {
      const opacity = element.attr('body/opacity');
      return opacity === 1;
    });

    if (highlightedElements.length === 0) return;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    highlightedElements.forEach((element) => {
      const bbox = element.getBBox();
      if (bbox) {
        minX = Math.min(minX, bbox.x);
        minY = Math.min(minY, bbox.y);
        maxX = Math.max(maxX, bbox.x + bbox.width);
        maxY = Math.max(maxY, bbox.y + bbox.height);
      }
    });

    if (minX === Infinity) return;

    const bbox = {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };

    let containerRect;
    if (document.fullscreenElement) {
      containerRect = document.fullscreenElement.getBoundingClientRect();
    } else {
      containerRect = this.paperContainer.getBoundingClientRect();
    }
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    const mapWidth = bbox.width;
    const mapHeight = bbox.height;
    const padding = 50;
    const targetZoomX = (containerWidth - padding) / mapWidth;
    const targetZoomY = (containerHeight - padding) / mapHeight;
    const targetZoom = Math.min(targetZoomX, targetZoomY);
    const constrainedZoom = Math.max(PAPER_ZOOM_SETTINGS.min, Math.min(PAPER_ZOOM_SETTINGS.max, targetZoom));
    this.paperScroller.zoom(constrainedZoom, { absolute: true });
    const centerX = bbox.x + bbox.width / 2;
    const centerY = bbox.y + bbox.height / 2;
    this.paperScroller.center(centerX, centerY);
  }
}

export default KitchenSinkService;
